
import React from "react";
import { useLists } from "@/context/ListContext";
import { useTasks, Task } from "@/context/TaskContext";
import TaskBoard from "./TaskBoard";
import { Skeleton } from "../ui/skeleton";
import { useTaskDetailModal } from "./TaskDetailModalContext";

const TaskContentArea = React.memo(function TaskContentArea({ switching = false }: { switching?: boolean }) {
  const { activeListId, lists } = useLists();
  const { tasks, loading, updateTaskStatus, setActiveTask, activeTask, reorderTasks } = useTasks();
  const { open } = useTaskDetailModal();

  const selectedList = React.useMemo(() => lists.find(l => l.id === activeListId), [lists, activeListId]);

  // When a card is selected, open the detail modal for the task
  const handleTaskSelect = React.useCallback((taskId: string) => {
    const selectedTask = tasks.find(t => t.id === taskId);
    if (selectedTask) {
      setActiveTask(selectedTask);
    }
  }, [tasks, setActiveTask]);

  // Handle drag and drop between columns and positions
  const handleDragEnd = React.useCallback((result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    const fromIndex = source.index;
    const toIndex = destination.index;

    if (fromStatus === toStatus && fromIndex === toIndex) {
      // No change
      return;
    }

    // If moving within the same column, reorder tasks
    if (fromStatus === toStatus) {
      reorderTasks(draggableId, fromIndex, toIndex, fromStatus);
    } else {
      // Moved to another column/status
      updateTaskStatus(draggableId, toStatus, toIndex);
    }
  }, [reorderTasks, updateTaskStatus]);

  if (!activeListId || !selectedList) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-gray-500 font-medium">
          ← Select a list to view tasks
        </div>
      </div>
    );
  }

  // Loading skeleton during switch
  if (switching || loading) {
    return (
      <div className="h-full w-full p-6 flex flex-col">
        <div className="flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex flex-col gap-3">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-6 flex flex-col">
      <div className="flex-1 min-h-0">
        <TaskBoard
          tasks={tasks}
          onTaskSelect={handleTaskSelect}
          activeTaskId={activeTask?.id}
          loading={loading}
          onDragEnd={handleDragEnd}
        />
      </div>
    </div>
  );
});

export default TaskContentArea;
